import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Software company banner v4
// Left: rotating headline slides + a services nav (real links).
// Right: a radial service diagram that swaps to match whichever
// service link is hovered/focused (or clicked, before navigation).
// Background: animated data-network mesh (unchanged from v3).

const SLIDES = [
  { title: "Crafting Digital Experiences That Inspire", subtitle: "Your Vision, Our Expertise" },
  { title: "Empower Your Online Presence", subtitle: "Innovative Solutions Await" },
  { title: "Elevate Your Efficiency", subtitle: "Transformative Software, Tailored to You" },
];

const TEAL = "#0AB1B9";
const ORANGE = "#FF8A3D";

const SERVICES = [
  {
    key: "web-design",
    label: "Website Designing",
    href: "/web-design",
    hub: { type: "browser", label: "design" },
    satellites: [
      { type: "palette", label: "palette" },
      { type: "typography", label: "typography" },
      { type: "layers", label: "layout" },
      { type: "cursor", label: "prototype" },
    ],
  },
  {
    key: "web-development",
    label: "Web Application Development",
    href: "/web-development",
    hub: { type: "code", label: "app" },
    satellites: [
      { type: "browser", label: "frontend" },
      { type: "server", label: "backend" },
      { type: "db", label: "database" },
      { type: "git", label: "version control" },
    ],
  },
  {
    key: "mobile-development",
    label: "Mobile Application Development",
    href: "/mobile-development",
    hub: { type: "phone", label: "app" },
    satellites: [
      { type: "cloud", label: "cloud sync" },
      { type: "db", label: "data" },
      { type: "bell", label: "push alerts" },
      { type: "code", label: "native code" },
    ],
  },
  {
    key: "graphic-design",
    label: "Graphic Design",
    href: "/grapic-design",
    hub: { type: "palette", label: "visuals" },
    satellites: [
      { type: "brush", label: "illustration" },
      { type: "image", label: "imagery" },
      { type: "typography", label: "typography" },
      { type: "shapes", label: "branding" },
    ],
  },
  {
    key: "digital-marketing",
    label: "Digital Marketing",
    href: "/digital-marketing",
    hub: { type: "chart", label: "growth" },
    satellites: [
      { type: "target", label: "audience" },
      { type: "megaphone", label: "campaigns" },
      { type: "share", label: "social" },
      { type: "search", label: "SEO" },
    ],
  },
];

// ---- shared stroked icon set, 24x24 viewbox ----
function IconPath({ type, color, size = 22 }) {
  const c = { fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  const s = size / 24;
  if (type === "typography") {
    return (
      <text x="0" y="18" fontSize="16" fontFamily="'Space Grotesk', sans-serif" fontWeight="600" fill={color} transform={`scale(${s})`}>
        Aa
      </text>
    );
  }
  return (
    <g transform={`scale(${s})`}>
      {type === "cloud" && <path {...c} d="M7 18h10a4 4 0 0 0 .3-8 5.5 5.5 0 0 0-10.6-1.6A4 4 0 0 0 7 18Z" />}
      {type === "server" && (
        <>
          <rect {...c} x="3" y="4" width="18" height="6" rx="1.4" />
          <rect {...c} x="3" y="14" width="18" height="6" rx="1.4" />
          <circle cx="7" cy="7" r="0.9" fill={color} stroke="none" />
          <circle cx="7" cy="17" r="0.9" fill={color} stroke="none" />
        </>
      )}
      {type === "db" && (
        <>
          <ellipse {...c} cx="12" cy="6" rx="7" ry="3" />
          <path {...c} d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
          <path {...c} d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
        </>
      )}
      {type === "shield" && <path {...c} d="M12 2.5 19.5 5.5V11c0 5-3.4 8.7-7.5 11-4.1-2.3-7.5-6-7.5-11V5.5Z" />}
      {type === "router" && (
        <>
          <circle {...c} cx="12" cy="12" r="3.2" />
          <path {...c} d="M12 3v3.2M12 17.8V21M4 8l2.7 1.6M17.3 14.4 20 16M4 16l2.7-1.6M17.3 9.6 20 8" />
        </>
      )}
      {type === "cpu" && (
        <>
          <rect {...c} x="6" y="6" width="12" height="12" rx="2" />
          <rect {...c} x="9" y="9" width="6" height="6" rx="1" />
          <path {...c} d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
        </>
      )}
      {type === "git" && (
        <>
          <circle {...c} cx="6" cy="6" r="2" />
          <circle {...c} cx="6" cy="18" r="2" />
          <circle {...c} cx="18" cy="12" r="2" />
          <path {...c} d="M6 8v8M6 12c4 0 6-3 6-3" />
        </>
      )}
      {type === "code" && <path {...c} d="M8 6 3 12l5 6M16 6l5 6-5 6M14 4l-4 16" />}
      {type === "browser" && (
        <>
          <rect {...c} x="2.5" y="4" width="19" height="16" rx="2" />
          <path {...c} d="M2.5 8.5h19" />
          <circle cx="5.2" cy="6.2" r="0.6" fill={color} stroke="none" />
          <circle cx="7.2" cy="6.2" r="0.6" fill={color} stroke="none" />
        </>
      )}
      {type === "layers" && (
        <>
          <path {...c} d="M12 3 3 8l9 5 9-5-9-5Z" />
          <path {...c} d="M3 13l9 5 9-5" />
        </>
      )}
      {type === "palette" && (
        <>
          <path {...c} d="M12 3a9 9 0 1 0 0 18c1.4 0 2-1 2-2s-.6-1.3-.6-2 .5-1.3 1.6-1.3H17a4 4 0 0 0 4-4c0-4.4-4-8.7-9-8.7Z" />
          <circle cx="7.5" cy="10.5" r="1.1" fill={color} stroke="none" />
          <circle cx="9.5" cy="7" r="1.1" fill={color} stroke="none" />
          <circle cx="14.5" cy="7" r="1.1" fill={color} stroke="none" />
        </>
      )}
      {type === "cursor" && <path {...c} d="M5 3v17l4.2-4.2L12 21l2-1-3-5.5H16Z" />}
      {type === "brush" && <path {...c} d="M4 20l3-3 9-9a2 2 0 0 1 3 3l-9 9-3 3-2-1Z M14.5 6.5l3 3" />}
      {type === "image" && (
        <>
          <rect {...c} x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.2" cy="8.2" r="1.6" {...c} fill="none" />
          <path {...c} d="M21 15l-5.5-5.5L9 16" />
        </>
      )}
      {type === "shapes" && (
        <>
          <circle {...c} cx="7" cy="7" r="4" />
          <rect {...c} x="13" y="3" width="8" height="8" rx="1.2" />
          <path {...c} d="M9 21l4-8 4 8Z" />
        </>
      )}
      {type === "bell" && (
        <>
          <path {...c} d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
          <path {...c} d="M10 20a2 2 0 0 0 4 0" />
        </>
      )}
      {type === "chart" && <path {...c} d="M3 16l6-6 4 4 8-8M15 6h6v6" />}
      {type === "target" && (
        <>
          <circle {...c} cx="12" cy="12" r="9" />
          <circle {...c} cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.4" fill={color} stroke="none" />
        </>
      )}
      {type === "megaphone" && (
        <>
          <path {...c} d="M3 10v4h4l7 5V5l-7 5H3Z" />
          <path {...c} d="M18.5 9.2a4 4 0 0 1 0 5.6" />
        </>
      )}
      {type === "share" && (
        <>
          <circle {...c} cx="6" cy="12" r="2.2" />
          <circle {...c} cx="18" cy="6" r="2.2" />
          <circle {...c} cx="18" cy="18" r="2.2" />
          <path {...c} d="M8 11l8-4M8 13l8 4" />
        </>
      )}
      {type === "search" && (
        <>
          <circle {...c} cx="10.5" cy="10.5" r="6.5" />
          <path {...c} d="M15.5 15.5 21 21" />
        </>
      )}
      {type === "phone" && (
        <>
          <rect {...c} x="7" y="2" width="10" height="20" rx="2.2" />
          <path {...c} d="M11 19h2" />
        </>
      )}
    </g>
  );
}

// ---- animated background network (unchanged) ----
const NODES = [
  { x: 80, y: 70, type: "cloud" }, { x: 230, y: 36, type: "code" }, { x: 400, y: 96, type: "server" },
  { x: 610, y: 46, type: "shield" }, { x: 730, y: 150, type: "db" }, { x: 120, y: 230, type: "cpu" },
  { x: 340, y: 270, type: "router" }, { x: 560, y: 240, type: "git" }, { x: 690, y: 330, type: "server" },
  { x: 210, y: 390, type: "cloud" }, { x: 460, y: 410, type: "db" }, { x: 60, y: 430, type: "code" },
];
const EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8],
  [2, 6], [3, 7], [6, 9], [7, 10], [9, 10], [1, 6],
];

function NetworkBackground() {
  return (
    <svg viewBox="0 0 800 480" preserveAspectRatio="xMidYMid slice" width="100%" height="100%"
      style={{ position: "absolute", inset: 0 }} aria-hidden="true">
      {EDGES.map(([a, b], i) => {
        const n1 = NODES[a], n2 = NODES[b];
        const color = i % 2 === 0 ? TEAL : ORANGE;
        return (
          <g key={i}>
            <line x1={n1.x} y1={n1.y} x2={n2.x} y2={n2.y} stroke={color} strokeOpacity="0.16" strokeWidth="1" />
            <circle r="2.6" fill={color} style={{
              offsetPath: `path('M ${n1.x} ${n1.y} L ${n2.x} ${n2.y}')`,
              animation: `pulseMove ${5 + (i % 5)}s linear ${i * 0.35}s infinite`,
              filter: "drop-shadow(0 0 3px currentColor)",
            }} />
          </g>
        );
      })}
      {NODES.map((n, i) => {
        const color = i % 2 === 0 ? TEAL : ORANGE;
        return (
          <g key={i} transform={`translate(${n.x}, ${n.y})`}>
            <circle r="14" fill={color} opacity="0" style={{ animation: `nodeGlow 3.4s ease-in-out ${i * 0.25}s infinite` }} />
            <g transform="translate(-9,-9)" opacity="0.75"><IconPath type={n.type} color={color} size={18} /></g>
          </g>
        );
      })}
    </svg>
  );
}

// ---- radial service diagram: hub + satellites, swaps per active service ----
function ServiceDiagram({ service, armed, transitionKey }) {
  const cx = 240, cy = 150, r = 108;
  const sats = useMemo(() => {
    const n = service.satellites.length;
    return service.satellites.map((s, i) => {
      const angle = -90 + i * (360 / n);
      const rad = (angle * Math.PI) / 180;
      return { ...s, x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad), angle };
    });
  }, [service]);

  return (
    <svg viewBox="0 0 480 320" width="100%" height="100%" role="img"
      aria-label={`${service.label} service diagram: ${service.hub.label} connected to ${service.satellites.map((s) => s.label).join(", ")}`}
      style={{ overflow: "visible" }}>
      <g key={transitionKey} style={{ animation: "sceneIn 0.5s ease-out both" }}>
        {sats.map((s, i) => {
          const d = `M ${cx} ${cy} L ${s.x} ${s.y}`;
          const color = i % 2 === 0 ? TEAL : ORANGE;
          return (
            <g key={s.label}>
              <path d={d} stroke={color} strokeOpacity="0.3" strokeWidth="1.6" fill="none"
                style={{
                  strokeDasharray: 200,
                  strokeDashoffset: armed ? 0 : 200,
                  transition: `stroke-dashoffset 0.8s ease-out ${0.12 * i + 0.1}s`,
                }} />
              <circle r="3" fill={color} opacity={armed ? 1 : 0} style={{
                offsetPath: `path('${d}')`,
                animation: armed ? `pulseMove ${2.8 + (i % 3) * 0.5}s linear ${0.8 + i * 0.2}s infinite` : "none",
                filter: "drop-shadow(0 0 3px currentColor)",
              }} />
            </g>
          );
        })}

        {sats.map((s, i) => {
          const color = i % 2 === 0 ? TEAL : ORANGE;
          const labelBelow = s.y > cy;
          return (
            <g key={`n-${s.label}`} transform={`translate(${s.x}, ${s.y})`}>
              <circle r="20" fill="#0A1315" stroke={color} strokeOpacity="0.55" strokeWidth="1.4" />
              <g transform="translate(-10,-10)"><IconPath type={s.type} color={color} size={20} /></g>
              <text x="0" y={labelBelow ? 34 : -28} textAnchor="middle" fill="#8FA3A3" fontSize="11" fontFamily="'IBM Plex Mono', monospace">
                {s.label}
              </text>
            </g>
          );
        })}

        <g transform={`translate(${cx}, ${cy})`}>
          <circle r="30" fill="#0A1315" stroke={TEAL} strokeWidth="1.8" />
          <circle r="30" fill={TEAL} opacity="0.12" style={{ animation: "nodeGlow 2.6s ease-in-out infinite" }} />
          <g transform="translate(-13,-13)"><IconPath type={service.hub.type} color={TEAL} size={26} /></g>
          <text x="0" y="48" textAnchor="middle" fill={ORANGE} fontSize="12.5" fontFamily="'IBM Plex Mono', monospace" fontWeight="500">
            {service.hub.label}
          </text>
        </g>
      </g>
    </svg>
  );
}

export default function SoftwareBanner() {
  const [armed, setArmed] = useState(false);
  const [slide, setSlide] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setArmed(true), 150);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 4200);
    return () => clearInterval(id);
  }, []);

  const activeService = SERVICES[activeIdx];

  return (
    <div style={styles.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500&display=swap');

        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes slideIn { from { opacity: 0; transform: translateY(18px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes sceneIn { from { opacity: 0; transform: scale(0.96);} to { opacity: 1; transform: scale(1);} }
        @keyframes pulseMove { from { offset-distance: 0%; opacity: 0; } 8% { opacity: 1; } 92% { opacity: 1; } to { offset-distance: 100%; opacity: 0; } }
        @keyframes nodeGlow { 0%,100% { opacity: 0.12; r: 12; } 50% { opacity: 0.28; r: 16; } }
        .slide-title, .slide-sub { animation: slideIn 0.55s ease-out both; }
        .svc-link { position: relative; text-decoration: none; color: #C9D6D6; }
        .svc-link .bar { position: absolute; left: -14px; top: 2px; bottom: 2px; width: 2px; background: transparent; transition: background 0.25s; }
        .svc-link.active { color: #F5F7F7; }
        .svc-link.active .bar, .svc-link:hover .bar, .svc-link:focus-visible .bar { background: ${ORANGE}; }
        .svc-link:hover, .svc-link:focus-visible { color: #F5F7F7; }
        @media (max-width: 760px) {
          .banner-grid { grid-template-columns: 1fr !important; }
          .graph-col { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .slide-title, .slide-sub { animation: none !important; opacity: 1 !important; }
        }
      `}</style>

      <div style={styles.bgLayer}><NetworkBackground /></div>
      <div style={styles.vignette} aria-hidden="true" />

      <div className="banner-grid" style={styles.grid2}>
        <div style={styles.leftCol}>
          <div style={{ ...styles.eyebrow, animation: "fadeUp 0.6s ease-out 0.05s both" }}>
            <span style={styles.eyebrowDot} />
            software &amp; digital solutions
          </div>

          <div style={styles.slideWrap}>
            <h1 key={`t-${slide}`} className="slide-title" style={styles.h1}>{SLIDES[slide].title}</h1>
            <p key={`s-${slide}`} className="slide-sub" style={styles.sub}>{SLIDES[slide].subtitle}</p>
          </div>

          <div style={styles.dotNav}>
            {SLIDES.map((_, i) => (
              <span key={i} style={{
                display: "inline-block", height: 4, width: i === slide ? 22 : 8, borderRadius: 4,
                background: i === slide ? ORANGE : "rgba(245,247,247,0.25)", transition: "width 0.3s, background 0.3s",
              }} />
            ))}
          </div>

          <nav aria-label="Services" style={{ margin: "26px 0 30px", animation: "fadeUp 0.6s ease-out 0.35s both" }}>
            <ul style={styles.svcList}>
              {SERVICES.map((s, i) => (
                <li key={s.key} style={{ paddingLeft: 14 }}>
                  <a
                    href={s.href}
                    className={`svc-link${i === activeIdx ? " active" : ""}`}
                    style={styles.svcLink}
                    onMouseEnter={() => setActiveIdx(i)}
                    onFocus={() => setActiveIdx(i)}
                  >
                    <span className="bar" />
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div style={{ display: "flex", gap: 12, animation: "fadeUp 0.6s ease-out 0.5s both" }}>
            <button onClick={() => {navigate("/contact-us")}} style={styles.ctaPrimary}>Start your project →</button>
            <a href="#see-our-work" className="text-decoration-none" style={styles.ctaGhost}>See our work &nbsp; →</a>
          </div>
        </div>

        <div className="graph-col" style={styles.rightCol}>
          <ServiceDiagram service={activeService} armed={armed} transitionKey={activeService.key} />
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: "relative", width: "100%", minHeight: 500,
    background: "linear-gradient(180deg, #0C1618 0%, #0A1315 60%, #090F11 100%)",
    color: "#F5F7F7", fontFamily: "'Inter', sans-serif", overflow: "hidden",
    borderRadius: 0, boxSizing: "border-box", padding: "56px 56px",
  },
  bgLayer: { position: "absolute", inset: 0, opacity: 0.9 },
  vignette: {
    position: "absolute", inset: 0,
    background: "radial-gradient(ellipse at 24% 40%, rgba(10,177,185,0.10), transparent 55%), radial-gradient(ellipse at 78% 70%, rgba(255,138,61,0.08), transparent 55%), linear-gradient(180deg, rgba(9,15,17,0.15), rgba(9,15,17,0.65))",
  },
  grid2: { position: "relative", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 40, alignItems: "center" },
  leftCol: { display: "flex", flexDirection: "column" },
  eyebrow: {
    display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 12.5, letterSpacing: "0.08em", color: ORANGE, marginBottom: 18, width: "fit-content",
  },
  eyebrowDot: { width: 6, height: 6, borderRadius: "50%", background: TEAL, boxShadow: "0 0 8px 2px rgba(10,177,185,0.6)", display: "inline-block" },
  slideWrap: { minHeight: 148 },
  h1: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "clamp(30px, 4vw, 46px)",
    lineHeight: 1.12, letterSpacing: "-0.01em", margin: "0 0 14px",
    background: `linear-gradient(90deg, #F5F7F7, #F5F7F7 60%, ${TEAL})`,
    WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
  },
  sub: { fontSize: 17, lineHeight: 1.5, color: ORANGE, fontWeight: 500, margin: 0, maxWidth: 440 },
  dotNav: { display: "flex", gap: 6, marginTop: 18 },
  svcList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 },
  svcLink: { fontSize: 14.5, fontWeight: 500, fontFamily: "'Inter', sans-serif" },
  ctaPrimary: {
    fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: 14.5, padding: "12px 22px", borderRadius: 8,
    border: "none", background: `linear-gradient(90deg, ${TEAL}, #078a90)`, color: "#08100F", cursor: "pointer",
  },
  ctaGhost: {
    fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: 14.5, padding: "12px 22px", borderRadius: 8,
    border: `1px solid rgba(255,138,61,0.5)`, background: "transparent", color: ORANGE, cursor: "pointer",
  },
  rightCol: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 },
};